import { useId, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import {
  useAuth,
  useClassLearningLearners,
  useClassLearningProgress,
  useSaveClassSessionFeedback,
  type ClassFeedbackDraft,
} from "@bw/shared/hooks";
import type { Schema } from "@bw/shared/types/generated";
import { ProgramAcademyWorkspace } from "./program-academy-workspace";

export interface ProgramLearningSectionProps {
  programId: string;
}
export interface ClassFeedbackPanelProps {
  programId: string;
  enrollmentId: string;
  onClose: () => void;
}

/** Independent teaching access; does not depend on financial/attendance rosters. */
export function ProgramLearningSection({
  programId,
}: ProgramLearningSectionProps) {
  const { user, loading } = useAuth();
  if (loading || !user) return null;
  return (
    <ScopedLearningSection
      key={`${user.uid}:${programId}`}
      programId={programId}
    />
  );
}

function ScopedLearningSection({ programId }: ProgramLearningSectionProps) {
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const query = useClassLearningLearners(programId, { offset });
  if (
    query.isError &&
    isApiError(query.error) &&
    [401, 403, 404].includes(query.error.status)
  )
    return null;
  return (
    <Stack spacing={3}>
      <ProgramAcademyWorkspace programId={programId} />
      <Paper
        component="section"
        variant="outlined"
        sx={{ p: { xs: 2, sm: 3 } }}
      >
        <Stack spacing={2}>
          <Typography component="h2" variant="h6">
            Learner feedback
          </Typography>
          {query.isError ? (
            <Alert
              severity="warning"
              action={
                <Button size="small" onClick={() => void query.refetch()}>
                  Retry
                </Button>
              }
            >
              Learner feedback is unavailable right now.
            </Alert>
          ) : !query.data ? (
            <Typography role="status">Loading learners…</Typography>
          ) : (
            <>
              {query.data.items.length === 0 ? (
                <Typography>No course learners on this page.</Typography>
              ) : (
                <List disablePadding>
                  {query.data.items.map((learner) => (
                    <ListItemButton
                      key={learner.enrollment_id}
                      disabled={!learner.can_view_progress}
                      onClick={() => setSelected(learner.enrollment_id)}
                    >
                      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ overflowWrap: "anywhere" }}>
                          {learner.learner_display_name?.trim() || "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {learner.track_label}
                        </Typography>
                      </Stack>
                      {learner.state === "historical" ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="Historical enrollment"
                        />
                      ) : null}
                    </ListItemButton>
                  ))}
                </List>
              )}
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  disabled={query.data.offset <= 0}
                  onClick={() =>
                    setOffset(
                      Math.max(0, query.data!.offset - query.data!.limit),
                    )
                  }
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  disabled={
                    query.data.offset + query.data.limit >= query.data.total
                  }
                  onClick={() =>
                    setOffset(query.data!.offset + query.data!.limit)
                  }
                >
                  Next
                </Button>
              </Stack>
            </>
          )}
        </Stack>
        {selected ? (
          <ClassFeedbackPanel
            programId={programId}
            enrollmentId={selected}
            onClose={() => setSelected(null)}
          />
        ) : null}
      </Paper>
    </Stack>
  );
}

/** Scoped private feedback viewer/editor, mounted for one selected enrollment. */
export function ClassFeedbackPanel(props: ClassFeedbackPanelProps) {
  const { user, loading } = useAuth();
  if (loading || !user) return null;
  return (
    <ScopedFeedbackPanel
      key={`${user.uid}:${props.programId}:${props.enrollmentId}`}
      {...props}
    />
  );
}

function ScopedFeedbackPanel({
  programId,
  enrollmentId,
  onClose,
}: ClassFeedbackPanelProps) {
  const titleId = useId();
  const [offset, setOffset] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const query = useClassLearningProgress(programId, enrollmentId, { offset });
  const save = useSaveClassSessionFeedback(programId, enrollmentId);
  async function submit(draft: ClassFeedbackDraft): Promise<void> {
    setNotice(null);
    try {
      await save.mutateAsync(draft);
    } catch (error) {
      setNotice(
        isApiError(error) && [401, 403, 404, 409].includes(error.status)
          ? "The learning record or your access changed. Review the refreshed record before editing again."
          : "Couldn't save feedback. Your draft has been kept; try again.",
      );
      throw error;
    }
  }
  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-labelledby": titleId,
          sx: { width: { xs: "100%", sm: 620 }, maxWidth: "100vw" },
        },
      }}
    >
      <Stack spacing={2} sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction="row"
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography id={titleId} component="h2" variant="h6">
            Private learner feedback
          </Typography>
          <Button size="small" onClick={onClose}>
            Close
          </Button>
        </Stack>
        {notice ? <Alert severity="warning">{notice}</Alert> : null}
        {query.isError ? (
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          >
            This learning record is unavailable. Your access may have changed.
          </Alert>
        ) : !query.data ? (
          <Typography role="status">Loading learning records…</Typography>
        ) : (
          <FeedbackContent
            data={query.data}
            busy={save.isPending}
            onSave={submit}
            onPage={setOffset}
          />
        )}
      </Stack>
    </Drawer>
  );
}

function FeedbackContent({
  data,
  busy,
  onSave,
  onPage,
}: {
  data: Schema["ClassLearningProgress"];
  busy: boolean;
  onSave: (draft: ClassFeedbackDraft) => Promise<void>;
  onPage: (offset: number) => void;
}) {
  const dates = useMemo(() => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    };
    try {
      return {
        zone: data.timezone,
        format: new Intl.DateTimeFormat("en-US", {
          ...options,
          timeZone: data.timezone,
        }),
      };
    } catch {
      return {
        zone: "UTC (class timezone unavailable)",
        format: new Intl.DateTimeFormat("en-US", {
          ...options,
          timeZone: "UTC",
        }),
      };
    }
  }, [data.timezone]);
  function time(iso: string): string {
    const date = new Date(iso);
    return Number.isFinite(date.getTime())
      ? dates.format.format(date)
      : "Time unavailable";
  }
  return (
    <Stack spacing={2}>
      <Typography component="h3" variant="h6">
        {data.learner_display_name?.trim() || "—"}
      </Typography>
      <Typography>
        {data.class_label} · {data.track_label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Times shown in {dates.zone}.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Learner posting deadline: {time(data.learner_posting_deadline)}.
        Authorized staff can continue to write feedback afterward.
      </Typography>
      <Typography variant="body2">
        {data.counts.entitled_count} entitled sessions ·{" "}
        {data.counts.present_count} present · {data.counts.late_count} late ·{" "}
        {data.counts.absent_count} absent · {data.counts.unmarked_count}{" "}
        unmarked · {data.counts.cancelled_count} cancelled
      </Typography>
      {data.occurrences.length === 0 ? (
        <Typography>No sessions on this page.</Typography>
      ) : (
        data.occurrences.map((occurrence) => (
          <FeedbackRow
            key={`${occurrence.entitlement_id}:${occurrence.feedback?.revision ?? 0}`}
            occurrence={occurrence}
            canWrite={data.can_write_feedback}
            busy={busy}
            onSave={onSave}
            time={time}
          />
        ))
      )}
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          disabled={busy || data.offset <= 0}
          onClick={() => onPage(Math.max(0, data.offset - data.limit))}
        >
          Previous
        </Button>
        <Button
          size="small"
          disabled={busy || data.offset + data.limit >= data.total}
          onClick={() => onPage(data.offset + data.limit)}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );
}

function FeedbackRow({
  occurrence,
  canWrite,
  busy,
  onSave,
  time,
}: {
  occurrence: Schema["ClassLearningOccurrence"];
  canWrite: boolean;
  busy: boolean;
  onSave: (draft: ClassFeedbackDraft) => Promise<void>;
  time: (iso: string) => string;
}) {
  const feedback = occurrence.feedback;
  const [content, setContent] = useState(
    feedback?.content_state === "active" ? (feedback.content ?? "") : "",
  );
  const editable =
    canWrite &&
    occurrence.can_write_feedback &&
    occurrence.state !== "cancelled" &&
    (!feedback || feedback.can_edit);
  return (
    <Box
      component="section"
      sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}
    >
      <Stack spacing={1}>
        <Typography component="h4" variant="subtitle1">
          {time(occurrence.effective_start)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ends {time(occurrence.effective_end)} · {occurrence.attendance} ·{" "}
          {occurrence.state}
        </Typography>
        {feedback?.content_state === "redacted" ? (
          <Typography color="text.secondary">Feedback removed</Typography>
        ) : null}
        {editable ? (
          <Stack
            component="form"
            spacing={1}
            onSubmit={(event) => {
              event.preventDefault();
              if (busy || !content.trim()) return;
              void onSave({
                recurrence_start: occurrence.recurrence_start,
                expected_revision: feedback?.revision ?? 0,
                content,
              }).catch(() => {
                // The panel reports the failure; an unknown failure keeps this draft.
              });
            }}
          >
            <TextField
              label="Feedback"
              multiline
              minRows={3}
              value={content}
              disabled={busy}
              onChange={(event) => setContent(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 4000 } }}
              helperText="Private to this learner and authorized class staff."
            />
            <Button
              type="submit"
              size="small"
              variant="contained"
              disabled={busy || !content.trim()}
            >
              Save feedback
            </Button>
          </Stack>
        ) : feedback?.content_state === "active" ? (
          <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {feedback.content ?? "Feedback unavailable."}
          </Typography>
        ) : !feedback ? (
          <Typography color="text.secondary">
            No feedback recorded for this session.
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}
